import React from "react";
import { Link2, RefreshCw, Plus, Upload, Trash2 } from "lucide-react";
import { useComposerContext } from "../context/ComposerContext";

export default function ImagePopover({ applyImageLink, addImageToActiveRow, onReplaceActiveImage, onUploadToSlot, onRemoveActiveImage, setThumbnailRowSize }) {
  const { imagePopover, setImagePopover, imagePopoverRef } = useComposerContext();

  if (!imagePopover.open) return null;

  const { mode = "gallery", isPlaceholder = false } = imagePopover;
  const showSize = mode === "gallery";
  const showAdd = mode === "gallery";
  const showRemove = !isPlaceholder && mode === "gallery";

  return (
    <div
      ref={imagePopoverRef}
      data-bc-popover
      className="fixed z-[95] w-[calc(100vw-2rem)] sm:w-64 bg-white border border-bookish-purple/15 rounded-2xl shadow-xl p-3 space-y-3"
      style={{ top: imagePopover.top, left: Math.min(Math.max(8, imagePopover.left), window.innerWidth - 264) }}
    >
      <p className="text-[10px] font-semibold uppercase tracking-wider text-bookish-purple/50">Image Settings</p>

      {/* Upload / Replace buttons */}
      <div className="flex gap-1.5">
        {isPlaceholder ? (
          <button
            type="button"
            onClick={onUploadToSlot}
            className="flex-1 flex items-center justify-center gap-1 px-2 py-2 text-xs font-medium rounded-lg bg-bookish-purple text-white hover:bg-bookish-purpleAccent transition-colors"
          >
            <Upload size={13} /> Upload Image
          </button>
        ) : (
          <button
            type="button"
            onClick={onReplaceActiveImage}
            className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium rounded-lg border border-bookish-purple/15 hover:bg-bookish-purple/5 transition-colors"
          >
            <RefreshCw size={12} /> Replace
          </button>
        )}
        {showAdd && (
          <button
            type="button"
            onClick={addImageToActiveRow}
            className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium rounded-lg border border-bookish-purple/15 hover:bg-bookish-purple/5 transition-colors"
          >
            <Plus size={12} /> Add
          </button>
        )}
      </div>

      {/* Link URL */}
      {!isPlaceholder && (
        <div>
          <label className="text-xs block mb-1 text-bookish-purple/60">
            <Link2 size={11} className="inline mr-1" />
            Link URL
          </label>
          <input
            type="url"
            value={imagePopover.link}
            placeholder="https://example.com"
            onChange={(e) => setImagePopover((prev) => ({ ...prev, link: e.target.value }))}
            className="w-full border border-bookish-purple/15 rounded-lg px-2.5 py-1.5 text-sm bg-white focus:ring-1 focus:ring-bookish-purple/30"
          />
          <button type="button" onClick={applyImageLink} className="mt-1.5 px-2.5 py-1 text-xs font-medium rounded-lg bg-bookish-purple text-white hover:bg-bookish-purpleAccent transition-colors">
            Save link
          </button>
        </div>
      )}

      {/* Size toggle (gallery only) */}
      {showSize && !isPlaceholder && (
        <div>
          <label className="text-xs block mb-1.5 text-bookish-purple/60">Size</label>
          <div className="flex gap-1">
            {[
              ["small", "S"],
              ["medium", "M"],
              ["large", "L"],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setThumbnailRowSize(value)}
                className={`flex-1 px-2 py-1 text-xs font-medium rounded-lg border transition-colors ${
                  imagePopover.size === value
                    ? "bg-bookish-purple text-white border-bookish-purple"
                    : "border-bookish-purple/15 hover:bg-bookish-purple/5"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Remove image (gallery existing images only) */}
      {showRemove && (
        <div className="pt-1 border-t border-bookish-purple/8">
          <button
            type="button"
            onClick={onRemoveActiveImage}
            className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium rounded-lg text-red-500 hover:bg-red-50 transition-colors"
          >
            <Trash2 size={12} /> Remove Image
          </button>
        </div>
      )}
    </div>
  );
}
